import React, { useCallback, useMemo, useState } from 'react';
import { Steps, Button, message, Modal, Typography, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import ActionTypes from '@utils/deviceActions';
import { useSendDeviceActions } from '@hooks/useSendDeviceActions';
import useSwr from '@hooks/useSwr';
import { IDeviceActionModalProps, IOperatorsPriority } from './types';
import useActivation from './steps/Activation';
import useActionNetworkReset from './steps/NetworkReset';
import useActionRestriction from './steps/Restriction';
import useActionSimcardReset from './steps/SimcardReset';
import useActionPriorities from './steps/Priorities';
import useActionSmsBlock from './steps/SmsBlock';
import useActionSuspension from './steps/Suspension';
import useNetworkAction from './steps/NetworkBlock/NetworkAction';
import useNicknameChange from './steps/NicknameChange';
import useUpload from './steps/Upload';
import useValidation from './steps/Validation';
import * as Styled from './styles';
import './styles.less';
import useTransfer from './steps/Transfer';
import usePlasticChange from './steps/PlasticChange';
import useNumberChange from './steps/NumberChange';
import usePreActivation from './steps/PreActivation';
import useImeiLockUnlock from './steps/ImeiLockUnlock';
import useActivationMot from './steps/ActivationMot';
import useSuspensionWithdraw from './steps/SuspensionWithdraw';
import useForceReactivation from './steps/ForceReactivation';
import useFinancial from './steps/Financial';
import useAddCredit from './steps/AddCredit';
import useBrokerCancellation from './steps/BrokerCancellation';
import useMotDeactivation from './steps/MotDeactivation';
import useHlrReprog from './steps/HlrReprog';
import useChangeApn from './steps/ChangeApn';
import { useSendSms } from './steps/SendSms';
import useContractMovement from './steps/ContractMovement';
import useAlgarBaitRenewal from './steps/AlgarBaitRenewal';
import useSyncApn from './steps/SyncApn';
import useCancellation from './steps/Cancellation';

const { Text } = Typography;
const { Step } = Steps;

const DeviceActionModal: React.FC<IDeviceActionModalProps> = ({
  visible,
  actionKey,
  onClose,
  devicesProps,
}) => {
  const { t: translate } = useTranslation([
    'device-actions-modal',
    'device-actions',
  ]);

  const { data: dataOperators } = useSwr<IOperatorsPriority[]>(
    actionKey === ActionTypes.PRIORITY_OPERATOR
      ? '/service-proxy/operators'
      : null,
  );

  const { handleDeviceActions } = useSendDeviceActions();
  const [manualUpload, setManualUpload] = useState<Array<any>>([]);
  const [uploadedDevices, setUploadedDevices] = useState<any[]>([]);
  const [confirmDisabled, setConfirmDisabled] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState(0);

  const { activationData } = useActivation({
    handleDeviceActions,
    actionKey,
    uploadedDevicesState: [uploadedDevices, setUploadedDevices],
    currentStep,
    manualUploadDevices: [manualUpload, setManualUpload],
  });

  const { networkResetData } = useActionNetworkReset({
    handleDeviceActions,
    actionKey,
    uploadedDevices,
    devicesProps,
  });

  const { restrictionData } = useActionRestriction({
    handleDeviceActions,
    actionKey,
    uploadedDevices,
    currentStep,
    devicesProps,
  });

  const { simcardResetData } = useActionSimcardReset({
    handleDeviceActions,
    actionKey,
    devicesProps,
    uploadedDevices,
  });

  const { prioritiesData } = useActionPriorities({
    handleDeviceActions,
    actionKey,
    uploadedDevices,
    devicesProps,
    dataOperators,
  });

  const { smsBlockData } = useActionSmsBlock({
    handleDeviceActions,
    uploadedDevices,
    devicesProps,
  });

  const { suspensionData } = useActionSuspension({
    handleDeviceActions,
    actionKey,
    uploadedDevices,
    devicesProps,
  });

  const { networkBlockData } = useNetworkAction({
    handleDeviceActions,
    actionKey,
    uploadedDevicesState: [uploadedDevices, setUploadedDevices],
    currentStep,
    devicesProps,
  });

  const { nicknameChangeData } = useNicknameChange({
    handleDeviceActions,
    actionKey,
    uploadedDevices,
    devicesProps,
  });

  const { plasticChangeData } = usePlasticChange({
    handleDeviceActions,
    actionKey,
    uploadedDevices,
    devicesProps,
  });

  const { addCreditData } = useAddCredit({
    handleDeviceActions,
    actionKey,
    uploadedDevices,
    devicesProps,
  });

  const { numberChangeData } = useNumberChange({
    handleDeviceActions,
    actionKey,
    uploadedDevices,
    devicesProps,
  });

  const { preActivationData } = usePreActivation({
    handleDeviceActions,
    actionKey,
    uploadedDevices,
    devicesProps,
  });

  const { transferData } = useTransfer({
    uploadedDevices,
    devicesProps,
    currentStep,
  });

  const { imeiLockUnlockData } = useImeiLockUnlock({
    handleDeviceActions,
    uploadedDevicesState: [uploadedDevices, setUploadedDevices],
    devicesProps,
    currentStep,
  });

  const { activationMotData } = useActivationMot({
    handleDeviceActions,
    actionKey,
    currentStep,
    uploadedDevicesState: [uploadedDevices, setUploadedDevices],
    manualUploadDevices: [manualUpload, setManualUpload],
  });

  const { suspensionWithdrawData } = useSuspensionWithdraw({
    handleDeviceActions,
    devicesProps,
    uploadedDevices,
    currentStep,
  });

  const { forceReactivationData } = useForceReactivation({
    actionKey,
    devicesProps,
    uploadedDevices,
    handleDeviceActions,
  });

  const { financialData } = useFinancial({
    actionKey,
    devicesProps,
    handleDeviceActions,
  });

  const { brokerCancellationData } = useBrokerCancellation({
    actionKey,
    devicesProps,
    uploadedDevices,
    handleDeviceActions,
  });

  const { motDeactivationData } = useMotDeactivation({
    actionKey,
    devicesProps,
    uploadedDevices,
    handleDeviceActions,
  });

  const { hlrReprogData } = useHlrReprog({
    handleDeviceActions,
    actionKey,
    uploadedDevices,
    devicesProps,
    setUploadedDevices,
  });

  const { sendSmsData } = useSendSms({
    currentStep,
    uploadedDevices,
    selectedDevices: devicesProps,
    setUploadedDevices,
    actionKey,
  });

  const { contractMovementData } = useContractMovement({
    actionKey,
    uploadedDevicesState: [uploadedDevices, setUploadedDevices],
    currentStep,
    handleDeviceActions,
    manualUpload: devicesProps,
  });

  const { algarBaitRenewalData } = useAlgarBaitRenewal({
    handleDeviceActions,
    actionKey,
    uploadedDevices,
    devicesProps,
  });

  const { uploadStep, uploadReinitState } = useUpload({
    actionKey,
    setUploadedDevices,
  });

  const { changeApnData } = useChangeApn({
    currentStep,
    devicesProps,
    handleDeviceActions,
    uploadedDevices,
    setUploadedDevices,
  });

  const { syncApnData } = useSyncApn({
    handleDeviceActions,
    actionKey,
    uploadedDevices,
  });

  const { cancellationData } = useCancellation({ currentStep, devicesProps });

  const objtWithData: any = useMemo(() => {
    return {
      [ActionTypes.ACTIVATION_MULTI_CONNECT]: activationData,
      [ActionTypes.ACTIVATION_MOT]: activationMotData,
      [ActionTypes.NETWORK_RESET]: networkResetData,
      [ActionTypes.SIMCARD_RESET]: simcardResetData,
      [ActionTypes.PRIORITY_OPERATOR]: prioritiesData,
      [ActionTypes.OPERATOR_RESTRICTION]: restrictionData,
      [ActionTypes.SMS_BLOCK]: smsBlockData,
      [ActionTypes.CONTRACT_SUSPEND]: suspensionData,
      [ActionTypes.NETWORK_BLOCK]: networkBlockData,
      [ActionTypes.NICKNAME_CHANGE]: nicknameChangeData,
      [ActionTypes.TRANSFER]: transferData,
      [ActionTypes.PLASTIC_EXCHANGE]: plasticChangeData,
      [ActionTypes.MSISDN_EXCHANGE]: numberChangeData,
      [ActionTypes.MANUAL_ACTIVATION]: preActivationData,
      [ActionTypes.IMEI_LOCK]: imeiLockUnlockData,
      [ActionTypes.SUSPENSION]: suspensionWithdrawData,
      [ActionTypes.FORCE_REACTIVATION]: forceReactivationData,
      [ActionTypes.FINANCIAL_LOCK]: financialData,
      [ActionTypes.ADD_CREDIT_TO_SIMCARD]: addCreditData,
      [ActionTypes.BROKER_CANCELLATION]: brokerCancellationData,
      [ActionTypes.MOT_DEACTIVATION]: motDeactivationData,
      [ActionTypes.HLR_REPROG]: hlrReprogData,
      [ActionTypes.CHANGE_APN]: changeApnData,
      [ActionTypes.SEND_SMS]: sendSmsData,
      [ActionTypes.CONTRACT_MOVEMENT]: contractMovementData,
      [ActionTypes.ALGAR_BAIT_RENEWAL]: algarBaitRenewalData,
      [ActionTypes.SYNC_APN]: syncApnData,
      [ActionTypes.CANCELLATION]: cancellationData,
    };
  }, [
    activationData,
    activationMotData,
    networkResetData,
    simcardResetData,
    prioritiesData,
    restrictionData,
    smsBlockData,
    suspensionData,
    networkBlockData,
    nicknameChangeData,
    transferData,
    plasticChangeData,
    numberChangeData,
    preActivationData,
    imeiLockUnlockData,
    suspensionWithdrawData,
    forceReactivationData,
    financialData,
    addCreditData,
    brokerCancellationData,
    motDeactivationData,
    hlrReprogData,
    changeApnData,
    sendSmsData,
    contractMovementData,
    algarBaitRenewalData,
    syncApnData,
    cancellationData,
  ]);

  const { validationStep } = useValidation({
    devicesProps,
    uploadedDevices,
    manualUpload,
    actionKey,
    extraKey: objtWithData[actionKey]?.extraAction,
  });

  const handleVerifyRequerimentsForActions = useCallback(
    (actionSteps: any) => {
      if (actionKey === 31 || actionKey === 32) {
        return [...actionSteps];
      }
      if (
        actionKey === 0 ||
        actionKey === 1 ||
        actionKey === 20 ||
        actionKey === 21 ||
        actionKey === 22 ||
        actionKey === 23 ||
        actionKey === 45 ||
        actionKey === 17 ||
        actionKey === 18 ||
        actionKey === 39 ||
        actionKey === 100 ||
        actionKey === 124
      ) {
        return [...actionSteps, { ...validationStep }];
      }
      if (actionKey === ActionTypes.CANCELLATION) {
        return actionSteps;
      }
      if (actionSteps) {
        if (!devicesProps || devicesProps?.length === 0) {
          return [{ ...uploadStep }, ...actionSteps, { ...validationStep }];
        }
        return [...actionSteps, { ...validationStep }];
      }
      return [];
    },
    [uploadStep, devicesProps, validationStep, actionKey],
  );

  const handleReinitStates = useCallback(
    (actionRenitState?: any) => {
      setCurrentStep(0);
      setConfirmDisabled(false);

      // RESET ALL UPLOADED DEVICES
      uploadReinitState();
      if (actionRenitState) {
        actionRenitState();
      }
    },
    [uploadReinitState],
  );

  const disableNext = useMemo(() => {
    return uploadedDevices || devicesProps
      ? uploadedDevices.length > 0 || (devicesProps?.length || 0) > 0
      : false;
  }, [uploadedDevices, devicesProps]);

  const validateSteps = useMemo(() => {
    const selected = objtWithData[actionKey];

    const formatedSteps = handleVerifyRequerimentsForActions(
      selected?.contentSteps,
    );

    const nextStepDisabled = selected?.nextStepIsDisabled
      ? selected?.nextStepIsDisabled()
      : !disableNext;

    const reinitState = () => handleReinitStates(selected?.reinitState);

    const submitItem = () => selected.onSubmit();

    const titleName = selected?.extraAction ? selected.extraAction : actionKey;

    return {
      selected,
      formatedSteps,
      nextStepDisabled,
      reinitState,
      submitItem,
      titleName,
    };
  }, [
    actionKey,
    handleVerifyRequerimentsForActions,
    handleReinitStates,
    objtWithData,
    disableNext,
  ]);

  const handleExportReport = (axiosreturn: any) => {
    let csvContent = 'data:text/csv;charset=utf-8,';
    if (axiosreturn.data.errorList && axiosreturn.data.errorList?.[0]) {
      axiosreturn.data.errorList.forEach((str: string) => {
        csvContent += `${str}\r\n`;
      });
    }

    const hiddenElement = document.createElement('a');
    hiddenElement.href = encodeURI(csvContent);
    hiddenElement.target = '_blank';
    hiddenElement.download = 'data.csv';
    hiddenElement.click();
  };

  const handleConfirmAction = useCallback(async () => {
    setConfirmDisabled(true);

    const actionMessage =
      actionKey === 1
        ? translate(`device-actions:labels.activation`)
        : translate(`device-actions:labels.${validateSteps?.titleName}`);
    try {
      message.loading({
        content: translate('device-actions:messages.action-loading', {
          action:
            actionKey === 1
              ? translate(`device-actions:labels.activation`)
              : translate(`device-actions:labels.${actionKey}`),
        }),
        key: 'send-action',
        duration: 999,
      });

      const axiosreturn = await validateSteps.submitItem();

      if (axiosreturn && axiosreturn?.data) {
        message.destroy();
        if (axiosreturn.data.code && axiosreturn.data.code === '200') {
          message.success({
            content: translate('device-actions:messages.action-success', {
              action: actionMessage,
            }),
            key: 'send-action',
          });
        } else {
          Modal.warning({
            title: (
              <Text type="warning">
                {translate('device-actions:messages.action-warning', {
                  action: actionMessage,
                })}
              </Text>
            ),
            content: (
              <Space direction="vertical">
                <Text>
                  {axiosreturn.data.errorList
                    ? axiosreturn.data.errorList.map((str: string) => {
                        return (
                          <div key={str} style={{ marginBottom: '5px' }}>
                            {str}
                          </div>
                        );
                      })
                    : ''}
                </Text>
                <Button
                  type="primary"
                  onClick={() => handleExportReport(axiosreturn)}
                >
                  Exportar este relatório
                </Button>
              </Space>
            ),
          });
        }
      } else {
        message.success({
          content: translate('device-actions:messages.action-success', {
            action: actionMessage,
          }),
          key: 'send-action',
        });
      }
    } catch (error: any) {
      message.destroy();

      if (error?.response?.status === 413) {
        message.error({
          content: translate(
            'device-actions:messages.action-error-file-to-large',
          ),
          key: 'send-action',
        });
      } else {
        message.error({
          content: translate('device-actions:messages.action-error', {
            action: actionMessage,
          }),
          key: 'send-action',
        });
      }
    }
    setConfirmDisabled(false);
    onClose();
  }, [actionKey, onClose, translate, validateSteps]);

  const onNextStep = () => {
    setCurrentStep(previousState => previousState + 1);
  };

  const onPrevStep = () => {
    setCurrentStep(previousState => previousState - 1);
  };

  return (
    <Styled.ModalContainer
      width={actionKey !== ActionTypes.ACTIVATION_MOT ? 540 : 600}
      centered={actionKey !== ActionTypes.NETWORK_BLOCK}
      title={translate(`device-actions:labels.${validateSteps?.titleName}`)}
      open={visible}
      onOk={() => {
        validateSteps?.reinitState();
        onClose();
      }}
      onCancel={() => {
        validateSteps?.reinitState();
        onClose();
      }}
      afterClose={() => {
        validateSteps?.reinitState();
        document.body.style.removeProperty('overflow');
      }}
      footer={
        <div key="0">
          {currentStep > 0 && (
            <Button type="ghost" onClick={() => onPrevStep()}>
              {translate('previous')}
            </Button>
          )}
          {currentStep < validateSteps?.formatedSteps.length - 1 && (
            <Button
              type="primary"
              onClick={() => onNextStep()}
              disabled={validateSteps?.nextStepDisabled}
            >
              {translate('next')}
            </Button>
          )}
          {currentStep === validateSteps?.formatedSteps.length - 1 && (
            <Button
              type="primary"
              onClick={() => handleConfirmAction()}
              disabled={
                confirmDisabled || validateSteps?.selected?.confirmDisabled
              }
              loading={confirmDisabled}
            >
              {translate('confirm')}
            </Button>
          )}
        </div>
      }
    >
      {actionKey > -1 && (
        <>
          <Steps size="small" current={currentStep}>
            {validateSteps?.formatedSteps?.map((item: any) => (
              <Step key={item.title} title={item.title} />
            ))}
          </Steps>
          {validateSteps?.formatedSteps.length > 0 && (
            <div
              className="steps-content"
              style={{ marginTop: 16, padding: 8 }}
            >
              {validateSteps?.formatedSteps[currentStep].content}
            </div>
          )}
        </>
      )}
    </Styled.ModalContainer>
  );
};

export default DeviceActionModal;
